import React from "react";
import {Box, Typography} from "@mui/material";
import {GeoCity, GeoCountry} from "../../types/Geo";

type LngLat = [number, number];

const COUNTRY_SHAPES: Record<string, LngLat[]> = {
    BY: [
        [23.2, 51.3], [26.1, 51.25], [30.6, 51.5], [32.7, 52.1], [32.8, 53.1],
        [31.8, 55.2], [30.4, 56.15], [28.6, 56.17], [25.7, 56.05], [23.6, 55.2],
        [23.18, 53.9], [23.2, 51.3],
    ],
    GE: [
        [40.0, 43.4], [40.4, 43.58], [41.7, 43.38], [43.5, 42.98], [44.2, 42.4],
        [45.8, 42.05], [46.7, 41.27], [45.0, 41.15], [43.25, 41.05], [41.55, 41.45],
        [40.32, 41.52], [40.0, 43.4],
    ],
};

function project(lng: number, lat: number, width: number, height: number): [number, number] {
    return [((lng + 180) / 360) * width, ((90 - lat) / 180) * height];
}

function toPath(ring: LngLat[], width: number, height: number): string {
    return ring.map((point, index) => {
        const [x, y] = project(point[0], point[1], width, height);
        return (index === 0 ? "M" : "L") + x.toFixed(1) + " " + y.toFixed(1);
    }).join(" ") + " Z";
}

function boundsOf(ring: LngLat[]): {minLng: number; maxLng: number; minLat: number; maxLat: number} {
    return ring.reduce((acc, [lng, lat]) => ({
        minLng: Math.min(acc.minLng, lng),
        maxLng: Math.max(acc.maxLng, lng),
        minLat: Math.min(acc.minLat, lat),
        maxLat: Math.max(acc.maxLat, lat),
    }), {minLng: 180, maxLng: -180, minLat: 90, maxLat: -90});
}

export function WorldOperatingMap({countries, onSelectCountry}: {
    countries: GeoCountry[];
    onSelectCountry: (country: GeoCountry) => void;
}) {
    const width = 1000;
    const height = 500;
    return (
        <Box data-testid="operating-world-map">
            <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                Political map of countries where Relapka currently operates. Click a highlighted country.
            </Typography>
            <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="World political map" style={{width: "100%", background: "#cfe8f3", borderRadius: 8}}>
                {Array.from({length: 12}, (_, i) => (
                    <line key={"v" + i} x1={(i + 1) * (width / 13)} y1={0} x2={(i + 1) * (width / 13)} y2={height} stroke="#b7d7e8" strokeWidth={1}/>
                ))}
                {Array.from({length: 6}, (_, i) => (
                    <line key={"h" + i} x1={0} y1={(i + 1) * (height / 7)} x2={width} y2={(i + 1) * (height / 7)} stroke="#b7d7e8" strokeWidth={1}/>
                ))}
                {(Array.isArray(countries) ? countries : []).map((country) => {
                    const ring = COUNTRY_SHAPES[country.iso2];
                    if (!ring) {
                        return null;
                    }
                    const [lx, ly] = project(ring[0][0], ring[0][1], width, height);
                    return (
                        <g key={country.id}>
                            <path
                                d={toPath(ring, width, height)}
                                fill="#2e7d32"
                                stroke="#0b3d0b"
                                strokeWidth={1}
                                style={{cursor: "pointer"}}
                                onClick={() => onSelectCountry(country)}
                                data-testid={"country-shape-" + country.iso2}
                            >
                                <title>{country.name + " · " + country.listingCount + " listings"}</title>
                            </path>
                            <text x={lx + 8} y={ly - 6} fontSize={14} fontWeight={700} fill="#083008">{country.iso2}</text>
                        </g>
                    );
                })}
            </svg>
        </Box>
    );
}

export function CountryCitiesMap({country, onSelectCity}: {
    country: GeoCountry;
    onSelectCity: (city: GeoCity) => void;
}) {
    const ring = COUNTRY_SHAPES[country.iso2] || defaultRing(country);
    const box = boundsOf(ring);
    const padLng = Math.max(0.6, (box.maxLng - box.minLng) * 0.15);
    const padLat = Math.max(0.4, (box.maxLat - box.minLat) * 0.15);
    const minLng = box.minLng - padLng;
    const maxLng = box.maxLng + padLng;
    const minLat = box.minLat - padLat;
    const maxLat = box.maxLat + padLat;
    const width = 900;
    const height = 520;
    const localProject = (lng: number, lat: number): [number, number] => ([
        ((lng - minLng) / (maxLng - minLng)) * width,
        ((maxLat - lat) / (maxLat - minLat)) * height,
    ]);
    const path = ring.map((point, index) => {
        const [x, y] = localProject(point[0], point[1]);
        return (index === 0 ? "M" : "L") + x.toFixed(1) + " " + y.toFixed(1);
    }).join(" ") + " Z";

    return (
        <Box data-testid="country-cities-map">
            <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                {country.name}: cities currently in Relapka. Click a city to open its dashboard.
            </Typography>
            <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={country.name + " political map"} style={{width: "100%", background: "#d9efe3", borderRadius: 8}}>
                <path d={path} fill="#8bc48f" stroke="#1b5e20" strokeWidth={2}/>
                {(country.cities || []).map((city) => {
                    if (city.lat == null || city.lng == null) {
                        return null;
                    }
                    const [x, y] = localProject(city.lng, city.lat);
                    const r = 10 + Math.min(18, Math.sqrt(city.listingCount || 0) / 4);
                    return (
                        <g key={city.id} style={{cursor: "pointer"}} onClick={() => onSelectCity(city)} data-testid={"city-marker-" + city.id}>
                            <circle cx={x} cy={y} r={r} fill="#1565c0" stroke="#0d47a1" strokeWidth={2}/>
                            <text x={x} y={y + 4} textAnchor="middle" fontSize={11} fontWeight={700} fill="#fff">{city.listingCount}</text>
                            <text x={x + r + 6} y={y + 4} fontSize={14} fontWeight={600} fill="#0d47a1">{city.name}</text>
                        </g>
                    );
                })}
            </svg>
        </Box>
    );
}

function defaultRing(country: GeoCountry): LngLat[] {
    const cities = (country.cities || []).filter((city) => city.lat != null && city.lng != null);
    if (!cities.length) {
        return [[20, 40], [30, 40], [30, 50], [20, 50], [20, 40]];
    }
    const lngs = cities.map((c) => c.lng as number);
    const lats = cities.map((c) => c.lat as number);
    const minLng = Math.min(...lngs) - 1;
    const maxLng = Math.max(...lngs) + 1;
    const minLat = Math.min(...lats) - 1;
    const maxLat = Math.max(...lats) + 1;
    return [[minLng, minLat], [maxLng, minLat], [maxLng, maxLat], [minLng, maxLat], [minLng, minLat]];
}
